import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { DownloadSimple, WarningCircle, CreditCard, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (n, ccy = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "USD" }).format(Number(n || 0));

const shortDate = (d) => (d ? String(d).slice(0, 10) : "—");

export default function PublicInvoiceView() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const sessionId = params.get("session_id");
  const paidFlag = params.get("paid");
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const [paying, setPaying] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'polling' | 'paid' | 'failed' | null

  // `load` is memoized with the values it closes over so the polling
  // effect (below) can list it as a dependency without re-running on
  // every render. Previously the polling effect suppressed the hook
  // linter and any `load()` call inside the poll referenced whatever
  // `load` was defined at the last render — a subtle stale-closure
  // risk if the invoice id or token were ever swapped without a full
  // component remount. useCallback fixes that without changing behavior.
  const load = useCallback(() => {
    if (!token) {
      setState({ loading: false, error: "Missing view token", data: null });
      return;
    }
    axios
      .get(`${API}/invoices/${id}/public-view`, { params: { token } })
      .then((r) => setState({ loading: false, error: "", data: r.data }))
      .catch((e) =>
        setState({
          loading: false,
          error: e?.response?.data?.detail || "Unable to load invoice.",
          data: null,
        })
      );
  }, [id, token]);

  useEffect(() => { load(); }, [load]);

  // If we came back from Stripe, poll status until paid then reload the invoice
  useEffect(() => {
    if (!sessionId || paidFlag !== "1") return;
    setPaymentStatus("polling");
    let stopped = false;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaymentStatus("paid");
          load();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setPaymentStatus("failed");
          return;
        }
      } catch (err) {
        // Transient network hiccup — keep polling until the retry budget is exhausted.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[public-invoice] payment poll transient error", err);
        }
      }
      if (!stopped && tries < 20) setTimeout(poll, 1500);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, paidFlag, load]);

  const payNow = async () => {
    if (!state.data) return;
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/payments/invoice/checkout`, {
        invoice_id: id,
        token,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f4] text-[#7A7F8C] text-sm" data-testid="public-invoice-loading">
        Loading invoice…
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f4] p-6" data-testid="public-invoice-error">
        <div className="max-w-md w-full bg-white border border-[#e5e5e5] p-8 text-center">
          <WarningCircle size={32} weight="fill" className="text-[#B08048] mx-auto" />
          <h1 className="mt-4 text-lg font-semibold text-[#1B1E28]">Invoice unavailable</h1>
          <p className="mt-2 text-sm text-[#7A7F8C]">{state.error || "This link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  const { invoice: inv, firm, download_url } = state.data;
  const ccy = inv.currency || firm.currency || "USD";
  const statusLabel = (inv.status || "sent").toUpperCase();
  const statusColor =
    inv.status === "paid" ? "#0f766e" : inv.status === "void" ? "#7A7F8C" : "#B08048";
  const isPayable = inv.status !== "paid" && inv.status !== "void" && (inv.total || 0) > 0;

  return (
    <div className="min-h-screen bg-[#f5f5f4] py-10 px-4" data-testid="public-invoice-view">
      <div className="max-w-3xl mx-auto">
        {paymentStatus === "polling" && (
          <div className="mb-4 bg-white border border-[#B08048] px-4 py-3 text-sm text-[#1B1E28] flex items-center gap-2" data-testid="payment-polling-banner">
            <div className="w-2 h-2 bg-[#B08048] animate-pulse rounded-full" /> Confirming your payment with Stripe…
          </div>
        )}
        {paymentStatus === "paid" && (
          <div className="mb-4 bg-[#0f766e] text-white px-4 py-3 text-sm flex items-center gap-2" data-testid="payment-paid-banner">
            <CheckCircle size={16} weight="fill" /> Payment received — thank you.
          </div>
        )}
        {/* Action bar */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-[#7A7F8C]">
            Shared invoice · read-only
          </div>
          <div className="flex items-center gap-2">
            {isPayable && (
              <button
                onClick={payNow}
                disabled={paying}
                data-testid="public-invoice-pay-btn"
                className="inline-flex items-center gap-2 bg-[#B08048] text-[#1B1E28] text-sm font-semibold px-4 py-2 hover:bg-[#9C6C34] transition disabled:opacity-60"
              >
                <CreditCard size={14} weight="bold" /> {paying ? "Redirecting…" : `Pay ${fmt$(inv.total, ccy)} now`}
              </button>
            )}
            <a
              href={download_url}
              data-testid="public-invoice-download"
              className="inline-flex items-center gap-2 bg-[#1B1E28] text-white text-sm px-4 py-2 hover:bg-[#2a2e3b] transition"
            >
              <DownloadSimple size={14} weight="bold" /> Download PDF
            </a>
          </div>
        </div>

        <div className="bg-white border border-[#e5e5e5] shadow-sm">
          {/* Header */}
          <div className="px-10 py-8 border-b-2 border-[#B08048] flex items-start justify-between">
            <div>
              <div className="text-xl font-bold tracking-tight text-[#1B1E28]" data-testid="public-invoice-firm-name">
                {firm.name || "Advisory Firm"}
              </div>
              {firm.tagline && <div className="text-[0.6875rem] uppercase tracking-[0.14em] text-[#7A7F8C] mt-1">{firm.tagline}</div>}
            </div>
            <div className="text-right">
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-[#B08048]">Invoice</div>
              <div className="font-mono text-lg text-[#1B1E28] mt-1" data-testid="public-invoice-number">
                {inv.invoice_number}
              </div>
              <div
                className="inline-block mt-2 text-[0.625rem] font-mono uppercase tracking-[0.18em] px-2 py-0.5 text-white"
                style={{ background: statusColor }}
                data-testid="public-invoice-status"
              >
                {statusLabel}
              </div>
            </div>
          </div>

          {/* Meta grid */}
          <div className="px-10 py-6 grid grid-cols-3 gap-6 text-sm border-b border-[#e5e5e5]">
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-1">From</div>
              <div className="font-medium text-[#1B1E28]">{firm.name || "—"}</div>
              {firm.address && <div className="text-[#7A7F8C] text-xs whitespace-pre-line mt-0.5">{firm.address}</div>}
              {firm.email && <div className="text-[#7A7F8C] text-xs">{firm.email}</div>}
              {firm.phone && <div className="text-[#7A7F8C] text-xs">{firm.phone}</div>}
              {firm.website && <div className="text-[#7A7F8C] text-xs">{firm.website}</div>}
            </div>
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-1">Bill to</div>
              <div className="font-medium text-[#1B1E28]" data-testid="public-invoice-client">{inv.client_name}</div>
              {inv.engagement_name && <div className="text-[#7A7F8C] text-xs mt-0.5">{inv.engagement_name}</div>}
            </div>
            <div className="text-xs">
              <div className="flex justify-between py-0.5">
                <span className="text-[#7A7F8C]">Issue date</span>
                <span className="font-mono text-[#1B1E28]">{shortDate(inv.issue_date)}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-[#7A7F8C]">Due date</span>
                <span className="font-mono text-[#1B1E28]">{shortDate(inv.due_date)}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-[#7A7F8C]">Period</span>
                <span className="font-mono text-[#1B1E28]">
                  {shortDate(inv.period_start)} → {shortDate(inv.period_end)}
                </span>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="px-10 py-6">
            <table className="w-full text-sm" data-testid="public-invoice-items">
              <thead>
                <tr className="bg-[#1B1E28] text-white text-[0.625rem] font-mono uppercase tracking-[0.18em]">
                  <th className="text-left px-3 py-2 font-normal">Description</th>
                  <th className="text-right px-3 py-2 font-normal">Hours</th>
                  <th className="text-right px-3 py-2 font-normal">Rate</th>
                  <th className="text-right px-3 py-2 font-normal">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(inv.line_items || []).map((li, i) => (
                  <tr key={i} className="border-b border-[#f0f0f0]">
                    <td className="px-3 py-3 text-[#1B1E28]">{li.description}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#1B1E28]">{Number(li.hours || 0).toFixed(2)}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#1B1E28]">{fmt$(li.rate, ccy)}</td>
                    <td className="px-3 py-3 text-right font-mono text-[#1B1E28]">{fmt$(li.amount, ccy)}</td>
                  </tr>
                ))}
                {(inv.line_items || []).length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-3 py-6 text-center text-xs text-[#7A7F8C]">No line items.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-4 ml-auto max-w-xs text-sm">
              <div className="flex justify-between py-1">
                <span className="text-[#7A7F8C]">Subtotal</span>
                <span className="font-mono text-[#1B1E28]">{fmt$(inv.subtotal, ccy)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-[#7A7F8C]">Tax ({firm.tax_rate ?? 0}%)</span>
                <span className="font-mono text-[#1B1E28]">{fmt$(inv.tax_amount, ccy)}</span>
              </div>
              <div className="flex justify-between py-2 mt-1 border-t-2 border-[#1B1E28]">
                <span className="text-[#1B1E28] font-semibold">Total due</span>
                <span className="font-mono text-[#B08048] font-bold text-lg" data-testid="public-invoice-total">
                  {fmt$(inv.total, ccy)}
                </span>
              </div>
            </div>
          </div>

          {/* AI summary + send history */}
          {inv.ai_summary && (
            <div className="px-10 pb-4">
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-2">Work summary</div>
              <p className="text-sm text-[#1B1E28] leading-relaxed whitespace-pre-line">{inv.ai_summary}</p>
            </div>
          )}
          {(inv.send_history || []).length > 0 && (
            <div className="px-10 pb-6">
              <div className="text-[0.625rem] font-mono uppercase tracking-[0.18em] text-[#7A7F8C] mb-2">History</div>
              <ul className="text-xs text-[#7A7F8C] space-y-0.5" data-testid="public-invoice-history">
                {inv.send_history.map((h, i) => (
                  <li key={i} className="font-mono">
                    {shortDate(h.sent_at)} — sent to {h.recipient}
                    {h.sent_by ? ` by ${h.sent_by}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="px-10 py-5 border-t border-[#e5e5e5] text-xs text-[#7A7F8C]">
            {firm.invoice_footer || "Payment due within 30 days."}
          </div>
        </div>

        <div className="text-center text-[0.625rem] font-mono uppercase tracking-[0.2em] text-[#7A7F8C] mt-6">
          Powered by {firm.name || "YadidOS"}
        </div>
      </div>
    </div>
  );
}
